import axios from "axios";
import { pushUndo } from "./undo";
import { compressImage } from "./imageOpt";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const client = axios.create({ baseURL: API, timeout: 45000 });

const scanFD = async (file) => {
  const fd = new FormData();
  fd.append("file", await compressImage(file));
  return fd;
};

const findIn = async (path, id) => {
  try { return ((await client.get(path)).data || []).find((x) => x.id === id) || null; } catch { return null; }
};

const FIELD_LABELS = {
  selling_price: "prezzo dettaglio", wholesale_price: "prezzo ingrosso", company_price: "prezzo azienda",
  category: "categoria", sale_unit: "unità", vat_rate: "IVA", available: "disponibilità", in_shop: "visibilità",
  archived: "archivio", tray_pieces: "pezzi teglia", cooked_weight_g: "peso cotto", shelf_life_days: "scadenza", catalog: "listino", purchased: "origine", purchase_cost: "costo acquisto",
};
const patchDesc = (d) => {
  const parts = Object.keys(d).map((k) => FIELD_LABELS[k]).filter(Boolean);
  return parts.length ? ` (${parts.join(", ")})` : "";
};

client.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  config.headers["X-Lang"] = localStorage.getItem("lang") || "it";
  return config;
});

client.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401 && String(err?.config?.url || "").includes("/auth/me") && localStorage.getItem("token")) {
      localStorage.removeItem("token");
      if (window.location.pathname.startsWith("/app")) window.location.href = "/login";
    }
    if (err?.response?.status === 402 && !window.location.pathname.startsWith("/app/billing")) {
      window.location.href = "/app/billing";
    }
    return Promise.reject(err);
  }
);

export const api = {
  register: (data) => client.post("/auth/register", data).then((r) => r.data),
  login: (data) => client.post("/auth/login", data).then((r) => r.data),
  me: () => client.get("/auth/me").then((r) => r.data),
  forgotPassword: (email) => client.post("/auth/forgot-password", { email, origin_url: window.location.origin }).then((r) => r.data),
  resetPassword: (token, new_password) => client.post("/auth/reset-password", { token, new_password }).then((r) => r.data),
  loginHistory: () => client.get("/auth/logins").then((r) => r.data),
  staffFirstAccess: (token, payload) =>
    axios.post(`${API}/auth/staff/first-access`, payload, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.data),
  inviteEmployee: (eid, d) => client.post(`/employees/${eid}/invite`, { ...d, origin_url: window.location.origin }).then((r) => r.data),
  revokeEmployeeAccess: (eid) => client.post(`/employees/${eid}/revoke-access`).then((r) => r.data),
  setEmployeeAccessRole: (eid, access_role) => client.put(`/employees/${eid}/access-role`, { access_role }).then((r) => r.data),
  staffElevate: (secret) => client.post("/auth/staff/elevate", { secret }).then((r) => r.data),
  backupExport: () => client.get("/backup/export", { responseType: "blob" }).then((r) => r.data),
  verify2fa: (challenge_id, code) => client.post("/auth/verify-2fa", { challenge_id, code }).then((r) => r.data),

  ingredients: () => client.get("/ingredients").then((r) => r.data),
  foodcostList: () => client.get("/foodcost").then((r) => r.data),
  foodcostCreate: (name) => client.post("/foodcost", { name }).then((r) => r.data),
  foodcostSave: (id, d) => client.put(`/foodcost/${id}`, d).then((r) => r.data),
  foodcostDelete: (id) => client.delete(`/foodcost/${id}`).then((r) => r.data),
  connectionsMap: (params) => client.get("/connections/map", { params }).then((r) => r.data),
  networkShop: (lid) => client.get(`/network/links/${lid}/shop`).then((r) => r.data),
  networkPlaceOrder: (lid, d) => client.post(`/network/links/${lid}/order`, d).then((r) => r.data),
  networkMyOrders: (lid) => client.get(`/network/links/${lid}/orders`).then((r) => r.data),
  networkEditOrder: (lid, oid, d) => client.put(`/network/links/${lid}/orders/${oid}`, d).then((r) => r.data),
  networkDocs: (lid) => client.get(`/network/links/${lid}/docs`).then((r) => r.data),
  networkDocFull: (lid, kind, id) => client.get(`/network/links/${lid}/docs/${kind}/${id}`).then((r) => r.data),
  networkImportCatalog: (lid, data) => client.post(`/network/links/${lid}/import-catalog`, data).then((r) => r.data),
  specialDays: () => client.get("/special-days").then((r) => r.data),
  specialDayCreate: (data) => client.post("/special-days", data).then((r) => {
    if (r.data?.id) pushUndo(r.data.name || data.name || "—", () => client.delete(`/special-days/${r.data.id}`), () => client.post("/special-days", data));
    return r.data;
  }),
  specialDayUpdate: async (id, data) => {
    const prev = await findIn("/special-days", id);
    const res = (await client.put(`/special-days/${id}`, data)).data;
    if (prev) pushUndo(prev.name || "—", () => client.put(`/special-days/${id}`, prev), () => client.put(`/special-days/${id}`, data));
    return res;
  },
  specialDayDelete: async (id) => {
    const prev = await findIn("/special-days", id);
    const res = (await client.delete(`/special-days/${id}`)).data;
    if (prev) pushUndo(prev.name || "—", () => client.post("/special-days", prev));
    return res;
  },
  specialDayHistory: (id) => client.get(`/special-days/${id}/history`).then((r) => r.data),
  purchasedList: () => client.get("/purchased-products").then((r) => r.data),
  purchasedCreate: (data) => client.post("/purchased-products", data).then((r) => {
    if (r.data?.id) pushUndo(r.data.name || data.name || "—", () => client.delete(`/purchased-products/${r.data.id}`), () => client.post("/purchased-products", data));
    return r.data;
  }),
  purchasedUpdate: async (id, data) => {
    const prev = await findIn("/purchased-products", id);
    const res = (await client.put(`/purchased-products/${id}`, data)).data;
    if (prev) pushUndo(prev.name || "—", () => client.put(`/purchased-products/${id}`, prev), () => client.put(`/purchased-products/${id}`, data));
    return res;
  },
  purchasedDelete: async (id) => {
    const prev = await findIn("/purchased-products", id);
    const res = (await client.delete(`/purchased-products/${id}`)).data;
    if (prev) pushUndo(prev.name || "—", () => client.post("/purchased-products", prev));
    return res;
  },
  recipesBulkClean: (data) => client.post("/recipes/bulk-clean", data).then((r) => r.data),
  gallery: (dept, params) => client.get(`/gallery/${dept}`, { params }).then((r) => r.data),
  galleryFolderCreate: (dept, name) => client.post(`/gallery/${dept}/folders`, { name }).then((r) => r.data),
  galleryFolderRename: (fid, name) => client.patch(`/gallery/folders/${fid}`, { name }).then((r) => r.data),
  galleryFolderDelete: (fid) => client.delete(`/gallery/folders/${fid}`).then((r) => r.data),
  galleryUpload: (dept, file, folderId, name) => {
    const f = new FormData();
    f.append("file", file);
    if (folderId) f.append("folder_id", folderId);
    if (name) f.append("name", name);
    return client.post(`/gallery/${dept}/photos`, f, { timeout: 120000 }).then((r) => r.data);
  },
  galleryPhotoPatch: (pid, d) => client.patch(`/gallery/photos/${pid}`, d).then((r) => r.data),
  galleryPhotoDelete: (pid) => client.delete(`/gallery/photos/${pid}`).then((r) => r.data),
  recipePhotoFromGallery: (rid, photoId) => client.post(`/recipes/${rid}/photo-from-gallery`, { photo_id: photoId }).then((r) => r.data),
  deleteRecipePhoto: (rid) => client.delete(`/recipes/${rid}/photo`).then((r) => r.data),
  galleryAutoMatch: (dept, dryRun) => client.post(`/gallery/${dept}/auto-match`, { dry_run: dryRun }).then((r) => r.data),
  bulkAction: (section, ids, action) => client.post(`/bulk/${section}`, { ids, action }).then((r) => r.data),
  networkLinkPartner: (lid, pid) => client.put(`/network/links/${lid}/partner`, { partner_id: pid }).then((r) => r.data),
  deleteDepartment: (id) => client.delete(`/business/departments/${id}`).then((r) => r.data),
  priceNoticeApply: (id) => client.post(`/price-notices/${id}/apply`, {}, { timeout: 120000 }).then((r) => r.data),
  createIngredient: (d) => client.post("/ingredients", d).then((r) => {
    pushUndo(r.data.name, () => client.delete(`/ingredients/${r.data.id}`), () => client.post("/ingredients", d));
    return r.data;
  }),
  updateIngredient: async (id, d) => {
    const prev = await findIn("/ingredients", id);
    const res = (await client.put(`/ingredients/${id}`, d)).data;
    if (prev) pushUndo(prev.name, () => client.put(`/ingredients/${id}`, prev), () => client.put(`/ingredients/${id}`, d));
    return res;
  },
  deleteIngredient: async (id) => {
    const prev = await findIn("/ingredients", id);
    const res = (await client.delete(`/ingredients/${id}`)).data;
    if (prev) pushUndo(prev.name, () => client.post("/ingredients", prev));
    return res;
  },
  adjustStock: async (id, d) => {
    const res = (await client.post(`/ingredients/${id}/adjust`, d)).data;
    if (+d.delta) {
      const name = (await findIn("/ingredients", id))?.name || "—";
      pushUndo(`${name} (giacenza)`, () => client.post(`/ingredients/${id}/adjust`, { delta: -d.delta, reason: "undo" }), () => client.post(`/ingredients/${id}/adjust`, { delta: d.delta, reason: "redo" }));
    }
    return res;
  },
  assignSupplier: (id, supplier_id) => client.post(`/ingredients/${id}/supplier`, { supplier_id }).then((r) => r.data),

  recipes: (dept) => client.get("/recipes", { params: dept ? { dept } : {} }).then((r) => r.data),
  createRecipe: (d) => client.post("/recipes", d).then((r) => {
    pushUndo(r.data.name, () => client.delete(`/recipes/${r.data.id}`), () => client.post("/recipes", d));
    return r.data;
  }),
  updateRecipe: async (id, d, deptMode) => {
    const prev = await findIn("/recipes", id);
    const qs = deptMode ? `?dept_mode=${deptMode}` : "";
    const res = (await client.put(`/recipes/${id}${qs}`, d)).data;
    if (prev) pushUndo(prev.name, () => client.put(`/recipes/${id}`, prev), () => client.put(`/recipes/${id}${qs}`, d));
    return res;
  },
  deleteRecipe: async (id) => {
    const prev = await findIn("/recipes", id);
    const res = (await client.delete(`/recipes/${id}`)).data;
    if (prev) pushUndo(prev.name, () => client.post("/recipes", prev));
    return res;
  },

  partners: (kind) => client.get("/partners", { params: kind ? { kind } : {} }).then((r) => r.data),
  createPartner: (d) => client.post("/partners", d).then((r) => {
    if (r.data?.id) pushUndo(r.data.name || d.name || "—", () => client.delete(`/partners/${r.data.id}`), () => client.post("/partners", d));
    return r.data;
  }),
  updatePartner: async (id, d) => {
    const prev = await findIn("/partners", id);
    const res = (await client.put(`/partners/${id}`, d)).data;
    if (prev) pushUndo(prev.name, () => client.put(`/partners/${id}`, prev), () => client.put(`/partners/${id}`, d));
    return res;
  },
  partnerRelinkDocs: (id) => client.post(`/partners/${id}/relink-docs`).then((r) => r.data),
  deletePartner: async (id) => {
    const prev = await findIn("/partners", id);
    const res = (await client.delete(`/partners/${id}`)).data;
    if (prev) pushUndo(prev.name, () => client.post("/partners", prev));
    return res;
  },
  updateBusinessProfile: (d) => client.put("/business/profile", d).then((r) => r.data),
  uploadBusinessLogo: (file) => { const f = new FormData(); f.append("file", file); return client.post("/business/logo", f).then((r) => r.data); },
  deleteBusinessLogo: () => client.delete("/business/logo").then((r) => r.data),
  trackVideo: (d) => client.post("/videos/track", d).catch(() => {}),
  aiWasteSuggest: (d) => client.post("/recipes/ai-waste", d).then((r) => r.data),
  activePromo: () => client.get("/promo/active").then((r) => r.data),
  testimonials: () => client.get("/testimonials").then((r) => r.data),
  trackVisit: (path) => {
    try {
      const k = `visited_${path}`;
      if (sessionStorage.getItem(k)) return;
      sessionStorage.setItem(k, "1");
    } catch {}
    client.post("/track/visit", { path, ref: document.referrer || null }).catch(() => {});
  },
  blogList: () => client.get("/blog").then((r) => r.data),
  blogPost: (slug) => client.get(`/blog/${slug}`).then((r) => r.data),
  trashList: () => client.get("/trash").then((r) => r.data),
  trashRestore: (id) => client.post(`/trash/${id}/restore`).then((r) => r.data),
  trashPurge: (id) => client.delete(`/trash/${id}`).then((r) => r.data),
  trashCount: () => client.get("/trash/count").then((r) => r.data),
  trashEmptyAll: () => client.delete("/trash").then((r) => r.data),
  updateAccount: (d) => client.put("/auth/account", d).then((r) => r.data),
  revealPassword: (owner_code) => client.post("/auth/account/reveal-password", { owner_code }).then((r) => r.data),
  pushVapidKey: () => client.get("/push/vapid-key").then((r) => r.data),
  pushSubscribe: (subscription) => client.post("/push/subscribe", { subscription }).then((r) => r.data),
  pushUnsubscribe: (endpoint) => client.post("/push/unsubscribe", { endpoint }).then((r) => r.data),
  pushTest: () => client.post("/push/test").then((r) => r.data),
  posProducts: () => client.get("/pos/products").then((r) => r.data),
  createPosProduct: (d) => client.post("/pos/products", d).then((r) => {
    if (r.data?.id) pushUndo(r.data.name || d.name || "—", () => client.delete(`/pos/products/${r.data.id}`), () => client.post("/pos/products", d));
    return r.data;
  }),
  updatePosProduct: async (id, d) => {
    const prev = await findIn("/pos/products", id);
    const res = (await client.put(`/pos/products/${id}`, d)).data;
    if (prev) pushUndo(prev.name, () => client.put(`/pos/products/${id}`, prev), () => client.put(`/pos/products/${id}`, d));
    return res;
  },
  deletePosProduct: async (id) => {
    const prev = await findIn("/pos/products", id);
    const res = (await client.delete(`/pos/products/${id}`)).data;
    if (prev) pushUndo(prev.name, () => client.post("/pos/products", prev));
    return res;
  },
  importRecipesPos: () => client.post("/pos/products/import-recipes").then((r) => r.data),
  posProductPhoto: (id, file) => { const f = new FormData(); f.append("file", file); return client.post(`/pos/products/${id}/photo`, f).then((r) => r.data); },
  posByBarcode: (code) => client.get(`/pos/products/by-barcode/${encodeURIComponent(code)}`).then((r) => r.data),
  posOperatorLogin: (pin) => client.post("/pos/operator/login", { pin }).then((r) => r.data),
  posAuthorize: (pin) => client.post("/pos/authorize", { pin }).then((r) => r.data),
  receiptsExport: (start, end) => client.get("/receipts/export", { params: { start, end } }).then((r) => r.data),
  receiptsExportXml: (start, end) => client.get("/receipts/export/xml", { params: { start, end }, responseType: "blob" }).then((r) => r.data),
  receiptsExportEmail: (start, end, email) => client.post("/receipts/export/email", { start, end, email }).then((r) => r.data),
  sendEmployeeCode: (id, email) => client.post(`/employees/${id}/send-code`, { email }).then((r) => r.data),
  supportReport: (payload) => client.post("/support/report", payload).then((r) => r.data),
  createReceipt: (d) => client.post("/receipts", d).then((r) => r.data),
  voidReceipt: (id, reason, managerPin) => client.post(`/receipts/${id}/void`, { reason, manager_pin: managerPin }).then((r) => r.data),
  refundReceipt: (id, payload) => client.post(`/receipts/${id}/refund`, payload).then((r) => r.data),
  loyaltyCustomers: (q) => client.get("/loyalty/customers", { params: q ? { q } : {} }).then((r) => r.data),
  createLoyaltyCustomer: (d) => client.post("/loyalty/customers", d).then((r) => r.data),
  posStats: (date) => client.get("/stats/pos", { params: date ? { date } : {} }).then((r) => r.data),
  receipts: (date, q) => client.get("/receipts", { params: { ...(date ? { date } : {}), ...(q ? { q } : {}) } }).then((r) => r.data),
  receiptsClosure: (date) => client.get("/receipts/closure", { params: date ? { date } : {} }).then((r) => r.data),
  emailReceipt: (id, to) => client.post(`/receipts/${id}/email`, { to }).then((r) => r.data),
  docShareSig: (kind, id) => client.get(`/docs/share-sig/${kind}/${id}`).then((r) => r.data),
  countries: () => client.get("/countries").then((r) => r.data),
  setBusinessCountry: (code) => client.post("/business/country", { code }).then((r) => r.data),
  setPartnerCredentials: (id, d) => client.put(`/partners/${id}/credentials`, d).then((r) => r.data),
  partnerChangePassword: (token, new_password) =>
    axios.post(`${API}/auth/partner/change-password`, { new_password }, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.data),

  orders: (params) => client.get("/orders", { params: params || {} }).then((r) => r.data),
  resetPartnerData: (id) => client.post(`/partners/${id}/reset`).then((r) => r.data),
  createBizOrder: (d) => client.post("/orders", d).then((r) => {
    pushUndo(r.data.partner_name || "—", () => client.delete(`/orders/${r.data.id}`), () => client.post("/orders", d));
    return r.data;
  }),
  deleteOrder: async (id) => {
    const prev = await findIn("/orders", id);
    const res = (await client.delete(`/orders/${id}`)).data;
    if (prev) pushUndo(prev.partner_name || "—", () => client.post("/orders", prev));
    return res;
  },
  updateOrder: async (id, d) => {
    const prev = await findIn("/orders", id);
    const res = (await client.put(`/orders/${id}`, d)).data;
    if (prev) pushUndo(prev.partner_name || "—", () => client.put(`/orders/${id}`, prev), () => client.put(`/orders/${id}`, d));
    return res;
  },
  stockOverview: (dept) => client.get("/stock/overview", { params: dept ? { dept } : {} }).then((r) => r.data),
  stockDistribution: () => client.get("/stock/distribution").then((r) => r.data),
  setStockThreshold: (rid, value) => client.put(`/stock/products/${rid}/threshold`, { value }).then((r) => r.data),
  telegramSettings: (d) => client.put("/telegram/settings", d).then((r) => r.data),
  telegramDetect: () => client.post("/telegram/detect").then((r) => r.data),
  telegramTest: () => client.post("/telegram/test").then((r) => r.data),
  waSettings: () => client.get("/whatsapp/settings").then((r) => r.data),
  waSettingsSave: (recipients) => client.put("/whatsapp/settings", { recipients }).then((r) => r.data),
  waTest: () => client.post("/whatsapp/test").then((r) => r.data),
  directionCodeGet: () => client.get("/leftovers/direction-code").then((r) => r.data),
  quotesList: () => client.get("/quotes").then((r) => r.data),
  quoteCreate: (d) => client.post("/quotes", d).then((r) => r.data),
  quoteUpdate: (id, d) => client.put(`/quotes/${id}`, d).then((r) => r.data),
  quoteDelete: (id) => client.delete(`/quotes/${id}`).then((r) => r.data),
  quoteToOrder: (id) => client.post(`/quotes/${id}/to-order`).then((r) => r.data),
  b2bGetCode: () => client.get("/b2b/link-code").then((r) => r.data),
  b2bGenCode: () => client.post("/b2b/link-code").then((r) => r.data),
  b2bConnect: (code) => client.post("/b2b/connect", { code }).then((r) => r.data),
  b2bLinks: () => client.get("/b2b/links").then((r) => r.data),
  b2bApprove: (id) => client.post(`/b2b/links/${id}/approve`).then((r) => r.data),
  b2bReject: (id) => client.post(`/b2b/links/${id}/reject`).then((r) => r.data),
  b2bOrders: () => client.get("/b2b/orders").then((r) => r.data),
  directionCodeGen: () => client.post("/leftovers/direction-code").then((r) => r.data),
  posEndday: (date, dept) => client.get("/pos/endday", { params: { ...(date ? { date } : {}), ...(dept ? { dept } : {}) } }).then((r) => r.data),
  posGetFund: (dept) => client.get("/pos/fund", { params: dept ? { dept } : {} }).then((r) => r.data),
  posSetFund: (dept, amount, owner_code) => client.put("/pos/fund", { dept, amount, owner_code }).then((r) => r.data),
  posCashCount: (payload) => client.post("/pos/cash-count", payload).then((r) => r.data),
  stockAdjust: async (rid, qty) => {
    const prev = await findIn("/recipes", rid);
    const res = (await client.post(`/stock/products/${rid}/adjust`, { qty })).data;
    if (prev) pushUndo(`${prev.name} (giacenza)`, () => client.post(`/stock/products/${rid}/adjust`, { qty: Math.max(+prev.stock_qty || 0, 0) }), () => client.post(`/stock/products/${rid}/adjust`, { qty }));
    return res;
  },
  stockValueThreshold: (amount) => client.put("/stock/value-threshold", { amount }).then((r) => r.data),
  productionSuggested: () => client.get("/stock/production-suggested").then((r) => r.data),
  stockProductionHistory: (dept, granularity) => client.get("/stock/production-history", { params: { ...(dept ? { dept } : {}), granularity } }).then((r) => r.data),
  namesAiCheck: () => client.post("/names/ai-check").then((r) => r.data),
  namesAiApply: (changes) => client.post("/names/ai-apply", { changes }).then((r) => r.data),
  stockSetArea: (rid, area) => client.put(`/stock/products/${rid}/area`, { area }).then((r) => r.data),
  createStorageArea: (d) => client.post("/storage/areas", d).then((r) => r.data),
  renameStorageArea: (aid, name) => client.put(`/storage/areas/${aid}`, { name }).then((r) => r.data),
  deleteStorageArea: (aid) => client.delete(`/storage/areas/${aid}`).then((r) => r.data),
  addStorageItem: (aid, d) => client.post(`/storage/areas/${aid}/items`, d).then((r) => r.data),
  updateStorageItem: (aid, iid, d) => client.put(`/storage/areas/${aid}/items/${iid}`, d).then((r) => r.data),
  stockMoves: (rid) => client.get(`/stock/products/${rid}/moves`).then((r) => r.data),
  sciupo: (d) => client.post("/stock/sciupo", d).then((r) => r.data),
  sciupoList: (days = 30) => client.get(`/stock/sciupo?days=${days}`).then((r) => r.data),
  sciupoPhoto: async (file) => { const fd = await scanFD(file); return client.post("/stock/sciupo/photo", fd, { timeout: 120000 }).then((r) => r.data); },
  sciupoThreshold: (amount) => client.put("/stock/sciupo/threshold", { amount }).then((r) => r.data),
  stockLink: (rid) => client.get(`/stock/products/${rid}/link`).then((r) => r.data),
  stockSetLink: (rid, d) => client.put(`/stock/products/${rid}/link`, d).then((r) => r.data),
  recipesAutoLink: () => client.post("/recipes/auto-link").then((r) => r.data),
  sciupoStats: (month) => client.get(`/stock/sciupo/stats?month=${month}`).then((r) => r.data),
  stockFormulas: (dept) => client.get("/stock/formulas", { params: dept ? { dept } : {} }).then((r) => r.data),
  stockCreateProduct: (d) => client.post("/stock/products", d).then((r) => r.data),
  stockRenameProduct: (rid, name) => client.put(`/stock/products/${rid}/rename`, { name }).then((r) => r.data),
  stockMerge: (keep_id, merge_id) => client.post("/stock/products/merge", { keep_id, merge_id }).then((r) => r.data),
  devaiGenerate: (d) => client.post("/devai/generate", d, { timeout: 90000 }).then((r) => r.data),
  devaiModules: () => client.get("/devai/modules").then((r) => r.data),
  devaiModule: (mid) => client.get(`/devai/modules/${mid}`).then((r) => r.data),
  devaiDelete: (mid) => client.delete(`/devai/modules/${mid}`).then((r) => r.data),
  devaiAddRecord: (mid, data) => client.post(`/devai/modules/${mid}/records`, { data }).then((r) => r.data),
  devaiRecords: (mid) => client.get(`/devai/modules/${mid}/records`).then((r) => r.data),
  devaiDelRecord: (mid, rid) => client.delete(`/devai/modules/${mid}/records/${rid}`).then((r) => r.data),
  deleteStorageItem: (aid, iid) => client.delete(`/storage/areas/${aid}/items/${iid}`).then((r) => r.data),
  updateOrderStatus: (id, status, reason) => client.put(`/orders/${id}/status`, { status, reason: reason || null }).then((r) => r.data),
  productionSheet: (date, dept) => client.get("/production/sheet", { params: { ...(date ? { delivery_date: date } : {}), ...(dept ? { dept } : {}) } }).then((r) => r.data),
  referralStats: () => client.get("/referral/stats").then((r) => r.data),
  saveProductionSheet: (body) => client.post("/production/sheet/save", body).then((r) => r.data),
  productionHistory: () => client.get("/production/history").then((r) => r.data),
  productionHistoryOne: (id) => client.get(`/production/history/${id}`).then((r) => r.data),
  productionCompare: () => client.get("/production/compare").then((r) => r.data),
  markOrderPaid: (id) => client.post(`/orders/${id}/paid`).then((r) => r.data),
  issueInvoice: (id, weights) => client.post(`/orders/${id}/invoice`, weights ? { weights } : {}).then((r) => r.data),
  setSalesInvoicePayment: (id, status) => client.put(`/sales-invoices/${id}/payment`, { status }).then((r) => r.data),
  setInvoicePayment: (id, status) => client.put(`/invoices/${id}/payment`, { status }).then((r) => r.data),
  fiscalBackfill: () => client.post("/docs/fiscal-backfill").then((r) => r.data),
  dedupePartners: () => client.post("/partners/dedupe").then((r) => r.data),
  invoicesRollback: (ids) => client.post("/invoices/bulk-rollback", { ids }).then((r) => r.data),
  salesInvoices: () => client.get("/sales-invoices").then((r) => r.data),
  summaryInvoice: (d) => client.post("/sales-invoices/summary", d).then((r) => r.data),
  deleteSalesInvoice: (id) => client.delete(`/sales-invoices/${id}`).then((r) => r.data),
  deliveryNotes: () => client.get("/delivery-notes").then((r) => r.data),
  setDdtDate: (id, date) => client.patch(`/delivery-notes/${id}`, { date }).then((r) => r.data),
  setInvoiceDate: (id, date) => client.patch(`/sales-invoices/${id}`, { date }).then((r) => r.data),
  deleteDeliveryNote: (id) => client.delete(`/delivery-notes/${id}`).then((r) => r.data),
  productionSummary: (deliveryDate, dept) => client.get("/production/summary", { params: { ...(deliveryDate ? { delivery_date: deliveryDate } : {}), ...(dept ? { dept } : {}) } }).then((r) => r.data),
  weeklyPlan: (pid) => client.get(`/partners/${pid}/weekly-plan`).then((r) => r.data),
  saveWeeklyPlan: async (pid, d) => {
    let prev = null;
    try { prev = (await client.get(`/partners/${pid}/weekly-plan`)).data; } catch {}
    const res = (await client.put(`/partners/${pid}/weekly-plan`, d)).data;
    if (prev) pushUndo("Piano settimanale", () => client.put(`/partners/${pid}/weekly-plan`, prev), () => client.put(`/partners/${pid}/weekly-plan`, d));
    return res;
  },
  scaleRecipe: (id, qty) => client.get(`/recipes/${id}/scale`, { params: { qty } }).then((r) => r.data),
  deliveryNote: (id, weights) => client.post(`/orders/${id}/delivery-note`, weights ? { weights } : {}).then((r) => r.data),
  weightsHistory: () => client.get("/weights/history").then((r) => r.data),
  mergePartnerDuplicates: () => client.post("/partners/merge-duplicates").then((r) => r.data),

  invoices: () => client.get("/invoices").then((r) => r.data),
  createInvoice: (d) => client.post("/invoices", d).then((r) => {
    if (r.data?.id) pushUndo(r.data.supplier_name || d.supplier_name || "Fattura", () => client.delete(`/invoices/${r.data.id}`), () => client.post("/invoices", d));
    return r.data;
  }),
  backfillSuppliers: () => client.post("/invoices/backfill-suppliers").then((r) => r.data),
  deleteInvoice: async (id) => {
    const prev = await findIn("/invoices", id);
    const res = (await client.delete(`/invoices/${id}`)).data;
    if (prev) pushUndo(prev.supplier_name || "Fattura", () => client.post("/invoices", prev));
    return res;
  },
  extractInvoice: async (file) => {
    const fd = await scanFD(file);
    return client.post("/invoices/extract", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 }).then((r) => r.data);
  },
  priceCheck: (d) => client.post("/price-check", d).then((r) => r.data),
  priceMarket: (d) => client.post("/price-check/market", d).then((r) => r.data),
  assistantChat: (d) => client.post("/assistant/chat", d, { timeout: 120000 }).then((r) => r.data),
  assistantTts: (text, lang) => client.post("/assistant/tts", { text, lang }, { responseType: "blob", timeout: 60000 }).then((r) => r.data),
  lastPaidPrice: (q) => client.get("/price-last", { params: { q } }).then((r) => r.data),
  formulaFavorite: (id) => client.post(`/formula/${id}/favorite`).then((r) => r.data),
  formulaArchive: (id) => client.post(`/formula/${id}/archive`).then((r) => r.data),
  formulaPhoto: (fid, photo) => client.post(`/formula/${fid}/photo`, { photo }).then((r) => r.data),
  priceCompare: (q) => client.get("/price-compare", { params: q ? { q } : {} }).then((r) => r.data),
  networkGenerate: () => client.post("/network/generate").then((r) => r.data),
  networkRedeem: (code) => client.post("/network/redeem", { code }).then((r) => r.data),
  networkLinks: (suggest) => client.get(`/network/links${suggest ? "?suggest=1" : ""}`).then((r) => r.data),
  networkRevoke: (id) => client.delete(`/network/links/${id}`).then((r) => r.data),
  networkPricelist: () => client.get("/network/pricelist").then((r) => r.data),
  networkPricelistSave: (rid, d) => client.put(`/network/pricelist/${rid}`, d).then((r) => r.data),
  networkSuggestBulk: (pct, department) => client.post("/network/suggest-bulk", { pct, department }).then((r) => r.data),
  formulaSharePdf: (d) => client.post("/formula/share-pdf", d, { timeout: 60000 }).then((r) => r.data),
  formulaShareLog: (d) => client.post("/formula/share-log", d).then((r) => r.data),
  formulaShareHistory: (fid, name) => client.get("/formula/share-history", { params: { fid: fid || undefined, name: name || undefined } }).then((r) => r.data),
  formulaSharePdfLink: (d) => client.post("/formula/share-pdf-link", d, { timeout: 60000 }).then((r) => r.data),
  formulaCostSnapshot: (fid, d) => client.post(`/formula/${fid}/cost-snapshot`, d).then((r) => r.data),
  formulaCostHistory: (fid) => client.get(`/formula/${fid}/cost-history`).then((r) => r.data),
  formulaProduce: (d) => client.post("/formula/produce", d).then((r) => r.data),
  formulaProductionLog: (day) => client.get("/formula/production-log", { params: { day: day || new Date().toLocaleDateString("sv") } }).then((r) => r.data),
  formulaExplode: (id) => client.post(`/formula/${id}/explode`).then((r) => r.data),

  reports: () => client.get("/reports/summary").then((r) => r.data),

  billingPlans: () => axios.get(`${API}/billing/plans`).then((r) => r.data),
  checkout: (lookup_key) => client.post("/payments/checkout", { lookup_key, origin_url: window.location.origin }).then((r) => r.data),
  paymentStatus: (sessionId) => axios.get(`${API}/payments/status/${sessionId}`).then((r) => r.data),

  staffConfig: () => client.get("/staff/config").then((r) => r.data),
  setStaffConfig: (country) => client.put("/staff/config", { country }).then((r) => r.data),
  setPayrollRates: (payload) => client.put("/staff/payroll-rates", payload).then((r) => r.data),
  staffHolidays: (year) => client.get("/staff/holidays", { params: { year } }).then((r) => r.data),
  employees: () => client.get("/employees").then((r) => r.data),
  createEmployee: (d) => client.post("/employees", d).then((r) => r.data),
  updateEmployee: (id, d) => client.put(`/employees/${id}`, d).then((r) => r.data),
  auditLog: (entity = "") => client.get(`/audit-log?limit=150${entity ? `&entity=${entity}` : ""}`).then((r) => r.data),
  deleteEmployee: (id) => client.delete(`/employees/${id}`).then((r) => r.data),
  timesheet: (id, month, year) => client.get(`/employees/${id}/timesheet`, { params: { month, year } }).then((r) => r.data),
  generatePayslip: (d) => client.post("/payslips/generate", d).then((r) => r.data),
  payslips: (month, year) => client.get("/payslips", { params: { month, year } }).then((r) => r.data),
  deletePayslip: (id) => client.delete(`/payslips/${id}`).then((r) => r.data),
  clockInfo: (code) => axios.get(`${API}/clock/${code}`).then((r) => r.data),
  clockToggle: (code, pin, photo) => axios.post(`${API}/clock/${code}`, { pin, photo }).then((r) => r.data),
  setStaffPay: (d) => client.put("/staff/config", d).then((r) => r.data),
  shifts: (from_date, to_date) => client.get("/shifts", { params: { from_date, to_date } }).then((r) => r.data),
  createShift: (d) => client.post("/shifts", d).then((r) => {
    if (r.data?.id) pushUndo("Turno", () => client.delete(`/shifts/${r.data.id}`), () => client.post("/shifts", d));
    return r.data;
  }),
  deleteShift: (id) => client.delete(`/shifts/${id}`).then((r) => r.data),
  copyShifts: (d) => client.post("/shifts/copy", d).then((r) => r.data),
  suggestShifts: (week_start) => client.post("/shifts/suggest", { week_start }).then((r) => r.data),
  publicShifts: (code, pin) => axios.get(`${API}/shifts/public/${code}`, { params: { pin } }).then((r) => r.data),
  publicWeekShifts: (code) => axios.get(`${API}/shifts/public-week/${code}`).then((r) => r.data),
  monthlySummary: (month, year) => client.get("/staff/monthly-summary", { params: { month, year } }).then((r) => r.data),
  attendance: (month, year) => client.get("/attendance", { params: { month, year } }).then((r) => r.data),
  editTimeEntry: (id, d) => client.put(`/time-entries/${id}`, d).then((r) => r.data),
  createTimeEntry: (d) => client.post("/time-entries", d).then((r) => r.data),
  deleteTimeEntry: (id) => client.delete(`/time-entries/${id}`).then((r) => r.data),
  createAbsence: (d) => client.post("/absences", d).then((r) => r.data),
  absences: (month, year, employee_id) => client.get("/absences", { params: { month, year, employee_id } }).then((r) => r.data),
  deleteAbsence: (id) => client.delete(`/absences/${id}`).then((r) => r.data),
  preparations: () => client.get("/preparations").then((r) => r.data),
  createPreparation: (d) => client.post("/preparations", d).then((r) => {
    if (r.data?.id) pushUndo(r.data.name || d.name || "—", () => client.delete(`/preparations/${r.data.id}`), () => client.post("/preparations", d));
    return r.data;
  }),
  updatePreparation: async (id, d) => {
    const prev = await findIn("/preparations", id);
    const res = (await client.put(`/preparations/${id}`, d)).data;
    if (prev) pushUndo(prev.name || "—", () => client.put(`/preparations/${id}`, prev), () => client.put(`/preparations/${id}`, d));
    return res;
  },
  deletePreparation: async (id) => {
    const prev = await findIn("/preparations", id);
    const res = (await client.delete(`/preparations/${id}`)).data;
    if (prev) pushUndo(prev.name || "—", () => client.post("/preparations", prev));
    return res;
  },
  producePreparation: (id, qty, extra = {}) => client.post(`/preparations/${id}/produce`, { qty, ...extra }).then((r) => r.data),
  formulaList: (dept) => client.get("/formula", { params: dept ? { dept } : {} }).then((r) => r.data),
  formulaCreate: (d) => client.post("/formula", d).then((r) => {
    if (r.data?.id) pushUndo(r.data.name || d.name || "—", () => client.delete(`/formula/${r.data.id}`), () => client.post("/formula", d));
    return r.data;
  }),
  formulaUpdate: async (id, d) => {
    const prev = await findIn("/formula", id);
    const res = (await client.put(`/formula/${id}`, d)).data;
    if (prev) pushUndo(prev.name || "—", () => client.put(`/formula/${id}`, prev), () => client.put(`/formula/${id}`, d));
    return res;
  },
  formulaDelete: async (id) => {
    const prev = await findIn("/formula", id);
    const res = (await client.delete(`/formula/${id}`)).data;
    if (prev) pushUndo(prev.name || "—", () => client.post("/formula", prev));
    return res;
  },
  formulaScan: async (file, dept) => { const f = await scanFD(file); return client.post(`/formula/scan?dept=${dept || "bakery"}`, f, { timeout: 120000 }).then((r) => r.data); },
  formulaSyncPrice: (id, d) => client.post(`/formula/${id}/sync-price`, d).then((r) => r.data),
  formulaWeighing: (id, d) => client.post(`/formula/${id}/weighing`, d).then((r) => r.data),
  formulaApplyLoss: (id, d) => client.post(`/formula/${id}/apply-loss`, d).then((r) => r.data),
  formulaLossReport: () => client.get("/formula/loss-report").then((r) => r.data),
  publicTrace: (kind, id) => client.get(`/public/trace/${kind}/${id}`).then((r) => r.data),
  printProductLabel: (rid, body) => client.post(`/recipes/${rid}/product-label`, body).then((r) => r.data),
  orderLabels: (oid, body) => client.post(`/orders/${oid}/labels`, body).then((r) => r.data),
  labelsHistory: () => client.get("/labels/history").then((r) => r.data),
  partnerOrderHistory: (pid, start, end) => client.get(`/partners/${pid}/order-history`, { params: { start, end } }).then((r) => r.data),
  ordersHistoryAll: (start, end) => client.get("/orders/history-all", { params: { start, end } }).then((r) => r.data),
  ordersHistoryWeekly: (weeks) => client.get("/orders/history-weekly", { params: { weeks } }).then((r) => r.data),
  recipesBulkDept: (payload) => client.post("/recipes/bulk-department", payload).then((r) => {
    if (r.data.prev?.length) pushUndo(`Reparto × ${r.data.moved}`, () => client.post("/recipes/bulk-department", { items: r.data.prev }), () => client.post("/recipes/bulk-department", payload));
    return r.data;
  }),
  reorganizeDepts: () => client.post("/recipes/reorganize-departments", {}, { timeout: 120000 }).then((r) => r.data),
  reorganizeDeptsUndo: () => client.post("/recipes/reorganize-departments/undo").then((r) => r.data),
  formulaMoveDept: (fid, department) => client.post(`/formula/${fid}/move-department`, { department }).then((r) => {
    if (r.data.prev && r.data.prev !== department) pushUndo("Reparto formula", () => client.post(`/formula/${fid}/move-department`, { department: r.data.prev }), () => client.post(`/formula/${fid}/move-department`, { department }));
    return r.data;
  }),
  orderItemPrep: (oid, index, prepared) => client.post(`/orders/${oid}/item-prep`, { index, prepared }).then((r) => r.data),
  pastDocs: () => client.get("/past-docs").then((r) => r.data),
  pastDocUpload: (file, category) => { const f = new FormData(); f.append("file", file); if (category) f.append("category", category); return client.post("/past-docs/upload", f, { timeout: 120000 }).then((r) => r.data); },
  pastDocPatch: async (id, d) => {
    const prev = await findIn("/past-docs", id);
    const res = (await client.put(`/past-docs/${id}`, d)).data;
    if (prev) pushUndo(prev.number || prev.partner_name || "Documento", () => client.put(`/past-docs/${id}`, prev), () => client.put(`/past-docs/${id}`, d));
    return res;
  },
  pastDocManual: (d) => client.post("/past-docs/manual", d).then((r) => r.data),
  pastDocDelete: (id) => client.delete(`/past-docs/${id}`).then((r) => r.data),
  bulkSaleUnit: (d) => client.post("/catalog/bulk-sale-unit", d).then((r) => r.data),
  posImportable: () => client.get("/pos/products/importable").then((r) => r.data),
  posImportOne: (recipe_id) => client.post("/pos/products/import-one", { recipe_id }).then((r) => r.data),
  catalogLinkable: () => client.get("/catalog/linkable").then((r) => r.data),
  catalogFromPos: (pos_product_id) => client.post("/catalog/from-pos", { pos_product_id }).then((r) => r.data),
  catalogShowAll: () => client.post("/catalog/show-all").then((r) => r.data),
  createLot: (d) => client.post("/lots", d).then((r) => r.data),
  lots: (params = {}) => client.get("/lots", { params }).then((r) => r.data),
  expiringLots: (days = 7) => client.get(`/lots/expiring?days=${days}`).then((r) => r.data),
  deleteLot: (id) => client.delete(`/lots/${id}`).then((r) => r.data),
  barcodeLookup: (code) => client.get(`/barcode/${encodeURIComponent(code)}`).then((r) => r.data),
  scanCode: (code) => client.get(`/scan-code/${encodeURIComponent(code)}`).then((r) => r.data),
  prodLogLast: (params) => client.get("/production-log/last", { params }).then((r) => r.data),
  aiClear: (kind, ids) => client.post("/ai-review/clear", { kind, ids: ids || null }).then((r) => r.data),
  importStart: (d) => client.post("/formula/import/start", d).then((r) => r.data),
  importUpload: (batchId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post(`/formula/import/upload?batch_id=${batchId}`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 }).then((r) => r.data);
  },
  importBatches: (kind) => client.get(`/formula/import/batches?kind=${kind || "formula"}`).then((r) => r.data),
  importBatch: (bid) => client.get(`/formula/import/batches/${bid}`).then((r) => r.data),
  importRetry: (fid) => client.post(`/formula/import/retry/${fid}`).then((r) => r.data),
  importRetryBatch: (bid) => client.post(`/formula/import/retry-batch/${bid}`).then((r) => r.data),
  importVerify: (bid) => client.get(`/formula/import/verify/${bid}`).then((r) => r.data),
  formulaLinkIngredient: (fid, row, ingredient_id) => client.put(`/formula/${fid}/ingredient-link`, { row, ingredient_id }).then((r) => r.data),
  scanPartner: async (file) => {
    const fd = await scanFD(file);
    return client.post("/partners/scan", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 }).then((r) => r.data);
  },
  scanCatalog: async (file) => {
    const fd = await scanFD(file);
    return client.post("/catalog/scan", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 }).then((r) => r.data);
  },
  addCatalogItem: (d) => client.post("/catalog/items", d).then((r) => {
    pushUndo(r.data.name, () => client.delete(`/recipes/${r.data.id}`), () => client.post("/catalog/items", d));
    return r.data;
  }),
  wholesalePctGet: () => client.get("/catalog/wholesale-pct").then((r) => r.data),
  wholesalePctApply: (pct, department) => client.post("/catalog/wholesale-pct", { pct, department }).then((r) => r.data),
  patchCatalogItem: async (id, d) => {
    const prev = await findIn("/recipes", id);
    const res = (await client.put(`/recipes/${id}/catalog`, d)).data;
    if (prev) {
      const restore = {};
      for (const k of Object.keys(d)) {
        if (k === "available" || k === "in_shop") restore[k] = prev[k] !== false;
        else if (k === "archived" || k === "purchased") restore[k] = prev[k] === true;
        else if (k === "vat_rate") restore[k] = prev.vat_rate ?? -1;
        else if (k === "cat_mode") continue;
        else if (k === "category") restore[k] = prev.category || "";
        else if (k === "subcategory") restore[k] = prev.subcategory || "";
        else if (k === "department") restore[k] = prev.department || "bakery";
        else if (k === "catalog") restore[k] = prev.catalog || "standard";
        else if (k === "sale_unit") restore[k] = prev.sale_unit || "pcs";
        else restore[k] = +prev[k] || 0;
      }
      if (Object.keys(restore).length) {
        pushUndo(`${prev.name}${patchDesc(d)}`, () => client.put(`/recipes/${id}/catalog`, restore), () => client.put(`/recipes/${id}/catalog`, d));
      }
    }
    return res;
  },
  autoCategorizeRecipes: () => client.post("/recipes/auto-categorize", {}, { timeout: 90000 }).then((r) => r.data),
  aiOrganizeCatalog: (scope) => client.post("/catalog/ai-organize", { scope: scope || "products" }, { timeout: 120000 }).then((r) => r.data),
  renameCategory: (old, nw) => client.put("/catalog/categories/rename", { old, new: nw }).then((r) => r.data),
  deleteCategory: (name) => client.delete(`/catalog/categories/${encodeURIComponent(name)}`).then((r) => r.data),
  marketPrice: (rid, refresh) => client.post(`/recipes/${rid}/market-price${refresh ? "?refresh=true" : ""}`, {}, { timeout: 90000 }).then((r) => r.data),
  pricingRules: () => client.get("/catalog/pricing-rules").then((r) => r.data),
  savePricingRules: (d) => client.put("/catalog/pricing-rules", d).then((r) => r.data),
  massReprice: () => client.post("/catalog/mass-reprice", {}, { timeout: 120000 }).then((r) => r.data),
  syncPrices: () => client.post("/catalog/sync-prices", {}, { timeout: 120000 }).then((r) => r.data),
  repriceOrder: (id) => client.post(`/orders/${id}/reprice`).then((r) => r.data),
  scanStandingOrder: async (file) => {
    const fd = await scanFD(file);
    return client.post("/standing-orders/scan", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 }).then((r) => r.data);
  },
  expenses: (month, year) => client.get("/expenses", { params: { month, year } }).then((r) => r.data),
  createExpense: (d) => client.post("/expenses", d).then((r) => {
    if (r.data?.id) pushUndo(r.data.description || d.description || "Spesa", () => client.delete(`/expenses/${r.data.id}`), () => client.post("/expenses", d));
    return r.data;
  }),
  deleteExpense: async (id) => {
    const prev = await findIn("/expenses", id);
    const res = (await client.delete(`/expenses/${id}`)).data;
    if (prev) pushUndo(prev.description || prev.category || "Spesa", () => client.post("/expenses", prev));
    return res;
  },
  scanInventoryDoc: async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post("/inventory/scan-doc", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 240000 }).then((r) => r.data);
  },
  importInventoryDoc: (d) => client.post("/inventory/import-doc", d).then((r) => r.data),
  recipesCopyTo: (d) => client.post("/recipes/copy-to", d).then((r) => {
    const ids = (r.data?.created || []).map((c) => c.id);
    if (ids.length) pushUndo(`Copia ricette (${ids.length})`, async () => { for (const id of ids) await client.delete(`/recipes/${id}`); });
    return r.data;
  }),
  recipePriceHistory: (rid) => client.get(`/recipes/${rid}/price-history`).then((r) => r.data),
  scanRecipeBook: async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post("/recipes/scan-book", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 }).then((r) => r.data);
  },
  importRecipeBook: (payload) => client.post("/recipes/scan-book/import", payload).then((r) => {
    const ids = (r.data?.created || []).map((c) => c.id);
    if (ids.length) pushUndo(`Import quaderno (${ids.length})`, async () => { for (const id of ids) await client.delete(`/recipes/${id}`); });
    return r.data;
  }),
  scanRecipe: async (file) => {
    const fd = await scanFD(file);
    return client.post("/recipes/scan", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 }).then((r) => r.data);
  },
  scanReceipt: async (file) => {
    const fd = await scanFD(file);
    return client.post("/expenses/scan", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 }).then((r) => r.data);
  },
  estimateNutrition: (ids) => client.post("/ingredients/estimate-nutrition", { ids: ids || null }, { timeout: 120000 }).then((r) => r.data),
  creditNotes: () => client.get("/credit-notes").then((r) => r.data),
  payments: () => client.get("/payments").then((r) => r.data),
  createPayment: (d) => client.post("/payments", d).then((r) => {
    if (r.data?.id) pushUndo(r.data.description || r.data.partner_name || d.description || "Pagamento", () => client.delete(`/payments/${r.data.id}`), () => client.post("/payments", d));
    return r.data;
  }),
  updatePayment: async (id, d) => {
    const prev = await findIn("/payments", id);
    const res = (await client.put(`/payments/${id}`, d)).data;
    if (prev) pushUndo(prev.description || prev.partner_name || "Pagamento", () => client.put(`/payments/${id}`, prev), () => client.put(`/payments/${id}`, d));
    return res;
  },
  deletePayment: async (id) => {
    const prev = await findIn("/payments", id);
    const res = (await client.delete(`/payments/${id}`)).data;
    if (prev) pushUndo(prev.description || prev.partner_name || "Pagamento", () => client.post("/payments", prev));
    return res;
  },
  scanPayment: async (file) => {
    const fd = await scanFD(file);
    return client.post("/payments/scan", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 }).then((r) => r.data);
  },
  scanMenu: async (file) => {
    const fd = await scanFD(file);
    return client.post("/menu/scan", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 }).then((r) => r.data);
  },
  scanPosProducts: async (file) => {
    const fd = await scanFD(file);
    return client.post("/pos/products/scan", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 }).then((r) => r.data);
  },
  scanInventory: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post("/inventory/scan", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 }).then((r) => r.data);
  },
  scanStaffDoc: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post("/staff/scan", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 }).then((r) => r.data);
  },
  kitchenOrders: () => client.get("/kitchen/orders").then((r) => r.data),
  kitchenItemDone: (tid, iid) => client.put(`/kitchen/items/${tid}/${iid}/done`).then((r) => r.data),
  kitchenTableDone: (tid) => client.put(`/kitchen/tables/${tid}/done`).then((r) => r.data),
  tableQrCodes: () => client.get("/tables/qr-codes").then((r) => r.data),
  addTableQrCodes: (names) => client.post("/tables/qr-codes", { names }).then((r) => r.data),
  deleteTableQrCode: (id) => client.delete(`/tables/qr-codes/${id}`).then((r) => r.data),
  forgotPin: () => client.post("/security/forgot-pin").then((r) => r.data),
  resetPin: (code, newPin) => client.post("/security/reset-pin", { code, new_pin: newPin }).then((r) => r.data),
  publicCallWaiter: (code) => client.post(`/public/table/${code}/call`).then((r) => r.data),
  clearTableCall: (tid) => client.put(`/tables/${tid}/call/clear`).then((r) => r.data),
  requestBill: (tid, d) => client.put(`/tables/${tid}/request-bill`, d).then((r) => r.data),
  emailReceipt: (rid, email) => client.post(`/receipts-share/${rid}/email`, { email }).then((r) => r.data),
  kitchenHistory: () => client.get("/kitchen/history").then((r) => r.data),
  waiterTips: () => client.get("/waiters/tips").then((r) => r.data),
  saveNavOrder: (order) => client.put("/auth/nav-order", { order }).then((r) => r.data),
  saveNavPrefs: (prefs) => client.put("/auth/nav-prefs", prefs).then((r) => r.data),
  posTabs: () => client.get("/pos/tabs").then((r) => r.data),
  createPosTab: (d) => client.post("/pos/tabs", d).then((r) => r.data),
  deletePosTab: (id) => client.delete(`/pos/tabs/${id}`).then((r) => r.data),
  createReservation: (d) => client.post("/reservations", d).then((r) => r.data),
  reservations: (now) => client.get("/reservations", { params: now ? { now } : {} }).then((r) => r.data),
  setReservationStatus: (id, status) => client.put(`/reservations/${id}`, { status }).then((r) => r.data),
  deleteReservation: (id) => client.delete(`/reservations/${id}`).then((r) => r.data),
  publicBookingInfo: (bid, date) => client.get(`/public/booking/${bid}`, { params: date ? { date } : {} }).then((r) => r.data),
  publicBook: (bid, d) => client.post(`/public/booking/${bid}`, d).then((r) => r.data),
  resvAvailability: (date) => client.get("/resv/availability", { params: date ? { date } : {} }).then((r) => r.data),
  saveResvSettings: (d) => client.put("/resv/settings", d).then((r) => r.data),
  staffDeadlines: () => client.get("/staff/deadlines").then((r) => r.data),
  createStaffDeadline: (d) => client.post("/staff/deadlines", d).then((r) => r.data),
  updateStaffDeadline: (id, d) => client.put(`/staff/deadlines/${id}`, d).then((r) => r.data),
  deleteStaffDeadline: (id) => client.delete(`/staff/deadlines/${id}`).then((r) => r.data),
  sendPartnerMessage: (pid, d) => client.post(`/partners/${pid}/message`, d).then((r) => r.data),
  broadcastMessage: (d) => client.post("/partners/broadcast", d).then((r) => r.data),
  partnerMessages: (pid) => client.get("/messages", { params: { partner_id: pid } }).then((r) => r.data),
  createCreditNoteDirect: (d) => client.post("/credit-notes", d).then((r) => r.data),
  deleteCreditNote: (id) => client.delete(`/credit-notes/${id}`).then((r) => r.data),
  createCreditNote: (orderId, d) => client.post(`/orders/${orderId}/credit-note`, d).then((r) => r.data),
  recipeToPreparation: (id) => client.post(`/recipes/${id}/to-preparation`).then((r) => r.data),
  liveStats: () => client.get("/stats/live").then((r) => r.data),
  sendMonthlyEmail: () => client.post("/reports/monthly-email").then((r) => r.data),
  setSectionLock: (d) => client.put("/security/lock", d).then((r) => r.data),
  unlockSections: (pin) => client.post("/security/unlock", { pin }).then((r) => r.data),
  viewCosts: (pin) => client.post("/security/view-costs", { pin }).then((r) => r.data),
  viewCostsLock: () => client.post("/security/view-costs/lock").then((r) => r.data),
  setViewCode: (eid, code) => client.post(`/employees/${eid}/view-code`, { code }).then((r) => r.data),
  renameEmployee: (eid, name) => client.post(`/employees/${eid}/rename`, { name }).then((r) => r.data),
  delViewCode: (eid) => client.delete(`/employees/${eid}/view-code`).then((r) => r.data),
  ownerCode: () => client.get("/security/owner-code").then((r) => r.data),
  setOwnerCode: (code) => client.put("/security/owner-code", { code }).then((r) => r.data),
  disableSectionLock: (pin) => client.post("/security/disable", { pin }).then((r) => r.data),
  authorizePayslip: (id, signer_name) => client.post(`/payslips/${id}/authorize`, { signer_name }).then((r) => r.data),

  reorderGroups: () => client.get("/reorder/groups").then((r) => r.data),
  createReorder: (d) => client.post("/reorder", d).then((r) => r.data),
  piStatus: () => client.get("/periodic-inventory/status").then((r) => r.data),
  piSettings: (d) => client.put("/periodic-inventory/settings", d).then((r) => r.data),
  piSheet: () => client.get("/periodic-inventory/sheet").then((r) => r.data),
  piSubmit: (d) => client.post("/periodic-inventory", d, { timeout: 120000 }).then((r) => r.data),
  piReports: () => client.get("/periodic-inventory/reports").then((r) => r.data),
  piReport: (id) => client.get(`/periodic-inventory/reports/${id}`).then((r) => r.data),
  dsPending: () => client.get("/dept-shop/pending").then((r) => r.data),
  dsOverview: () => client.get("/dept-shop/overview").then((r) => r.data),
  dsSettings: (dept) => client.get(`/dept-shop/${dept}/settings`).then((r) => r.data),
  dsSaveSettings: (dept, d) => client.put(`/dept-shop/${dept}/settings`, d).then((r) => r.data),
  dsTrackOrder: (dept, d) => client.post(`/dept-shop/${dept}/track-order`, d).then((r) => r.data),
  dsOrders: (dept) => client.get(`/dept-shop/${dept}/orders`).then((r) => r.data),
  dsReceive: (dept, tid, d) => client.post(`/dept-shop/${dept}/receive/${tid}`, d).then((r) => r.data),
  dsLoad: (dept, d) => client.post(`/dept-shop/${dept}/load`, d).then((r) => r.data),
  dsStock: (dept) => client.get(`/dept-shop/${dept}/stock`).then((r) => r.data),
  dsStockUpd: async (dept, iid, d) => {
    const prev = await findIn(`/dept-shop/${dept}/stock`, iid);
    const res = (await client.put(`/dept-shop/${dept}/stock/${iid}`, d)).data;
    if (prev) {
      const restore = { qty: prev.qty, name: prev.name, unit: prev.unit, min_qty: prev.min_qty };
      if (prev.purchase_price != null && !prev.cost_hidden) restore.purchase_price = prev.purchase_price;
      pushUndo(prev.name || "—", () => client.put(`/dept-shop/${dept}/stock/${iid}`, restore), () => client.put(`/dept-shop/${dept}/stock/${iid}`, d));
    }
    return res;
  },
  dsStockDel: (dept, iid) => client.delete(`/dept-shop/${dept}/stock/${iid}`).then((r) => r.data),
  dsGlobalProducts: (dept) => client.get(`/dept-shop/${dept}/global-products`).then((r) => r.data),
  dsPullGlobal: (dept, d) => client.post(`/dept-shop/${dept}/pull-global`, d).then((r) => {
    if (r.data?.transfer_id) pushUndo("Trasferimento dal globale", () => client.post(`/stock/transfers/${r.data.transfer_id}/undo`));
    return r.data;
  }),
  dsReturnGlobal: (dept, d) => client.post(`/dept-shop/${dept}/return-global`, d).then((r) => {
    if (r.data?.transfer_id) pushUndo("Reso al globale", () => client.post(`/stock/transfers/${r.data.transfer_id}/undo`));
    return r.data;
  }),
  dsCopyTo: (dept, d) => client.post(`/dept-shop/${dept}/copy-to`, d).then((r) => {
    if (r.data?.transfer_id) pushUndo("Copia tra reparti", () => client.post(`/stock/transfers/${r.data.transfer_id}/undo`));
    return r.data;
  }),
  stockTransfers: (dept) => client.get("/stock/transfers", { params: dept ? { dept } : {} }).then((r) => r.data),
  invoiceSavings: () => client.get("/invoices/savings").then((r) => r.data),
  stockTransferUndo: (tid) => client.post(`/stock/transfers/${tid}/undo`).then((r) => r.data),
  formulaCopyTo: (d) => client.post("/formula/copy-to", d).then((r) => r.data),
  dsMoveTo: (dept, d) => client.post(`/dept-shop/${dept}/move-to`, d).then((r) => r.data),
  dsAllStock: () => client.get("/dept-shop/all-stock").then((r) => r.data),
  dsPricelist: (dept) => client.get(`/dept-shop/${dept}/pricelist`).then((r) => r.data),
  dsPriceUpd: async (dept, iid, d) => {
    let prev = null;
    try { prev = (((await client.get(`/dept-shop/${dept}/pricelist`)).data || {}).items || []).find((x) => x.id === iid) || null; } catch {}
    const res = (await client.put(`/dept-shop/${dept}/pricelist/${iid}`, d)).data;
    if (prev) {
      const restore = async () => {
        await client.put(`/dept-shop/${dept}/pricelist/${iid}`, { purchase_price: prev.purchase_price || 0 });
        if (prev.sell_manual) await client.put(`/dept-shop/${dept}/pricelist/${iid}`, { sell_price: prev.sell_price || 0 });
        else await client.put(`/dept-shop/${dept}/pricelist/${iid}`, { reset: true });
      };
      pushUndo(`${prev.name || "—"} (€)`, restore, () => client.put(`/dept-shop/${dept}/pricelist/${iid}`, d));
    }
    return res;
  },
  dsApplyMarkup: (dept, d) => client.post(`/dept-shop/${dept}/apply-markup`, d).then((r) => r.data),
  dsCopyPricelist: (dept, d) => client.post(`/dept-shop/${dept}/copy-pricelist`, d).then((r) => r.data),
  dsEodSheet: (dept, date) => client.get(`/dept-shop/${dept}/eod${date ? `?date=${date}` : ""}`).then((r) => r.data),
  dsEodSummary: (date) => client.get(`/dept-shop/eod-summary${date ? `?date=${date}` : ""}`).then((r) => r.data),
  dsDayOpen: (dept, date) => client.post(`/dept-shop/${dept}/day/open`, { date }).then((r) => r.data),
  dsEodUndo: (dept, date) => client.post(`/dept-shop/${dept}/eod/undo`, { date }).then((r) => r.data),
  dsWasteTop: (days = 30) => client.get(`/dept-shop/waste-top?days=${days}`).then((r) => r.data),
  dsEodSubmit: (dept, d) => client.post(`/dept-shop/${dept}/eod`, d).then((r) => r.data),
  dsReports: (dept) => client.get(`/dept-shop/${dept}/reports`).then((r) => r.data),
  dsWeekly: (dept) => client.get(`/dept-shop/${dept}/weekly`).then((r) => r.data),
  dsScanDoc: (dept, formData) => client.post(`/dept-shop/${dept}/scan-doc`, formData, { headers: { "Content-Type": "multipart/form-data" }, timeout: 240000 }).then((r) => r.data),
  dsDocs: (dept) => client.get(`/dept-shop/${dept}/docs`).then((r) => r.data),
  dsDocLoad: (dept, did, d) => client.post(`/dept-shop/${dept}/docs/${did}/load`, d).then((r) => r.data),
  dsDocDel: (dept, did) => client.delete(`/dept-shop/${dept}/docs/${did}`).then((r) => r.data),
  dsReport: (dept, rid) => client.get(`/dept-shop/${dept}/reports/${rid}`).then((r) => r.data),
  priceAlerts: () => client.get("/price-alerts").then((r) => r.data),
  priceNotices: () => client.get("/price-notices").then((r) => r.data),
  priceNoticesReadAll: () => client.post("/price-notices/read-all").then((r) => r.data),
  posAlertsCount: () => client.get("/pos/alerts-count").then((r) => r.data),
  uploadRecipePhoto: async (id, file) => {
    const fd = await scanFD(file);
    return client.post(`/recipes/${id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },

  waste: () => client.get("/waste").then((r) => r.data),
  portalStandingSchedule: (code, weeks = 4) => axios.get(`${API}/portal/${code}/standing-schedule?weeks=${weeks}`).then((r) => r.data),
  portalWeekChange: (code, sid, d) => axios.post(`${API}/portal/${code}/standing-orders/${sid}/week-change`, d).then((r) => r.data),
  standingSchedule: (weeks = 4) => client.get(`/standing-schedule?weeks=${weeks}`).then((r) => r.data),
  setWeekOverride: (sid, d) => client.put(`/standing-orders/${sid}/week-override`, d).then((r) => r.data),
  standingOrders: () => client.get("/standing-orders").then((r) => r.data),
  createStandingOrder: (d) => client.post("/standing-orders", d).then((r) => {
    pushUndo(r.data.partner_name || "—", () => client.delete(`/standing-orders/${r.data.id}`), () => client.post("/standing-orders", d));
    return r.data;
  }),
  updateStandingOrder: async (id, d) => {
    const prev = await findIn("/standing-orders", id);
    const res = (await client.put(`/standing-orders/${id}`, d)).data;
    if (prev) pushUndo(prev.partner_name || "—", () => client.put(`/standing-orders/${id}`, prev), () => client.put(`/standing-orders/${id}`, d));
    return res;
  },
  deleteStandingOrder: async (id) => {
    const prev = await findIn("/standing-orders", id);
    const res = (await client.delete(`/standing-orders/${id}`)).data;
    if (prev) pushUndo(prev.partner_name || "—", () => client.post("/standing-orders", prev));
    return res;
  },
  generateStandingOrder: (id) => client.post(`/standing-orders/${id}/generate`).then((r) => r.data),
  wasteSummary: () => client.get("/waste/summary").then((r) => r.data),
  createWaste: (d) => client.post("/waste", d).then((r) => {
    if (r.data?.id) pushUndo(r.data.ingredient_name || d.name || "—", () => client.delete(`/waste/${r.data.id}`), () => client.post("/waste", d));
    return r.data;
  }),
  deleteWaste: async (id) => {
    const prev = await findIn("/waste", id);
    const res = (await client.delete(`/waste/${id}`)).data;
    if (prev) pushUndo(prev.ingredient_name || "—", () => client.post("/waste", { ...prev, name: prev.ingredient_name }));
    return res;
  },
  priceHistory: (id) => client.get(`/ingredients/${id}/price-history`).then((r) => r.data),
  priceSuggestion: (id, target_pct) => client.get(`/recipes/${id}/price-suggestion`, { params: { target_pct } }).then((r) => r.data),

  // Closures (giorni di chiusura)
  closures: () => client.get("/closures").then((r) => r.data),
  addClosure: (d) => client.post("/closures", d).then((r) => {
    if (r.data?.id) pushUndo(d.date || "Chiusura", () => client.delete(`/closures/${r.data.id}`), () => client.post("/closures", d));
    return r.data;
  }),
  deleteClosure: (id) => client.delete(`/closures/${id}`).then((r) => r.data),
  setClosedWeekdays: (weekdays) => client.put("/closures/weekdays", { weekdays }).then((r) => r.data),

  // Employment contracts
  contractsMeta: () => client.get("/contracts/meta").then((r) => r.data),
  contracts: (employee_id) => client.get("/contracts", { params: employee_id ? { employee_id } : {} }).then((r) => r.data),
  generateContract: (d) => client.post("/contracts/generate", d).then((r) => r.data),
  deleteContract: (id) => client.delete(`/contracts/${id}`).then((r) => r.data),
  emailContract: (id, email) => client.post(`/contracts/${id}/email`, { email: email || null, origin_url: window.location.origin }).then((r) => r.data),

  // Sala / Tavoli
  waiters: () => client.get("/waiters").then((r) => r.data),
  createWaiter: (d) => client.post("/waiters", d).then((r) => r.data),
  updateWaiter: (id, d) => client.put(`/waiters/${id}`, d).then((r) => r.data),
  deleteWaiter: (id) => client.delete(`/waiters/${id}`).then((r) => r.data),
  waiterChangePassword: (token, new_password) =>
    axios.post(`${API}/auth/waiter/change-password`, { new_password }, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.data),
  salaMe: () => client.get("/sala/me").then((r) => r.data),
  menuItems: () => client.get("/menu").then((r) => r.data),
  createMenuItem: (d) => client.post("/menu", d).then((r) => {
    if (r.data?.id) pushUndo(r.data.name || d.name || "—", () => client.delete(`/menu/${r.data.id}`), () => client.post("/menu", d));
    return r.data;
  }),
  updateMenuItem: async (id, d) => {
    const prev = await findIn("/menu", id);
    const res = (await client.put(`/menu/${id}`, d)).data;
    if (prev) pushUndo(prev.name, () => client.put(`/menu/${id}`, prev), () => client.put(`/menu/${id}`, d));
    return res;
  },
  deleteMenuItem: async (id) => {
    const prev = await findIn("/menu", id);
    const res = (await client.delete(`/menu/${id}`)).data;
    if (prev) pushUndo(prev.name, () => client.post("/menu", prev));
    return res;
  },
  importMenu: (source) => client.post("/menu/import", { source }).then((r) => r.data),
  menuPhoto: (id, file) => { const f = new FormData(); f.append("file", file); return client.post(`/menu/${id}/photo`, f).then((r) => r.data); },
  tables: () => client.get("/tables").then((r) => r.data),
  openTable: (d) => client.post("/tables", d).then((r) => r.data),
  addTableItems: (id, items) => client.post(`/tables/${id}/items`, { items }).then((r) => r.data),
  removeTableItem: (id, iid) => client.delete(`/tables/${id}/items/${iid}`).then((r) => r.data),
  closeTable: (id, d) => client.post(`/tables/${id}/close`, d).then((r) => r.data),
  cancelTable: (id) => client.delete(`/tables/${id}`).then((r) => r.data),
  publicTable: (code) => axios.get(`${API}/public/table/${code}`).then((r) => r.data),
  publicTableOrder: (code, items) => axios.post(`${API}/public/table/${code}/order`, { items }).then((r) => r.data),

  // Portal (public)
  portalInfo: (code, caldaneKey) => axios.get(`${API}/portal/${code}`, { params: { caldane_key: caldaneKey || "" } }).then((r) => r.data),
  portalDocuments: (code) => axios.get(`${API}/portal/${code}/documents`).then((r) => r.data),
  portalDocumentFull: (code, kind, id) => axios.get(`${API}/portal/${code}/documents/${kind}/${id}`).then((r) => r.data),
  portalVersion: (code) => axios.get(`${API}/portal/${code}/version`).then((r) => r.data),
  portalOrder: (code, data) => axios.post(`${API}/portal/${code}/order`, data).then((r) => r.data),
  portalOrders: (code) => axios.get(`${API}/portal/${code}/orders`).then((r) => r.data),
  portalEditOrder: (code, oid, d) => axios.put(`${API}/portal/${code}/orders/${oid}`, d).then((r) => r.data),
  portalRespond: (code, oid, status) => axios.post(`${API}/portal/${code}/orders/${oid}/respond`, { status }).then((r) => r.data),
  portalCaldaneUnlock: (code, code_value) => axios.post(`${API}/portal/${code}/caldane-unlock`, { code_value }).then((r) => r.data),
  caldaneCodeGet: () => client.get("/caldane/code").then((r) => r.data),
  caldaneCodeSet: (code) => client.post("/caldane/code", { code }).then((r) => r.data),
  ddtEditItems: (id, payload) => client.put(`/delivery-notes/${id}/items`, payload).then((r) => r.data),
  repriceHistory: (d) => client.post("/admin/reprice-history", d).then((r) => r.data),
  deliveryNoteGet: (id) => client.get(`/delivery-notes/${id}`).then((r) => r.data),
  invoiceFromDdt: (id) => client.post(`/delivery-notes/${id}/invoice`).then((r) => r.data),
  markAdeSent: (id) => client.post(`/sales-invoices/${id}/ade`).then((r) => r.data),
  leftoversList: (days = 7, day) => client.get("/leftovers", { params: day ? { days, day } : { days } }).then((r) => r.data),
  portalLeftoversToday: (code, day = null) => axios.get(`${API}/portal/${code}/leftovers/today${day ? `?day=${day}` : ""}`).then((r) => r.data),
  portalLeftoversSave: (code, d) => axios.post(`${API}/portal/${code}/leftovers`, d).then((r) => r.data),
  portalLeftoversSummary: (code, days = 7) => axios.get(`${API}/portal/${code}/leftovers/summary?days=${days}`).then((r) => r.data),
  portalLeftoversHistory: (code, days = 30, month = null) => axios.get(`${API}/portal/${code}/leftovers/history?days=${days}${month ? `&month=${month}` : ""}`).then((r) => r.data),
  torteSettingsGet: () => client.get("/torte/settings").then((r) => r.data),
  torteSettingsSet: (d) => client.put("/torte/settings", d).then((r) => r.data),
  portalPay: (code, oid) => axios.post(`${API}/portal/${code}/orders/${oid}/pay`, { origin_url: window.location.origin }).then((r) => r.data),
  portalPayDeposit: (code, oid) => axios.post(`${API}/portal/${code}/orders/${oid}/pay`, { origin_url: window.location.origin, deposit: true }).then((r) => r.data),
  portalCustomOrder: (code, data) => axios.post(`${API}/portal/${code}/custom-order`, data).then((r) => r.data),
  portalQuoteRespond: (code, oid, accept) => axios.post(`${API}/portal/${code}/orders/${oid}/quote-respond`, { accept }).then((r) => r.data),
  portalStandingList: (code) => axios.get(`${API}/portal/${code}/standing-orders`).then((r) => r.data),
  portalStandingCreate: (code, d) => axios.post(`${API}/portal/${code}/standing-orders`, d).then((r) => r.data),
  portalStandingUpdate: (code, sid, d) => axios.put(`${API}/portal/${code}/standing-orders/${sid}`, d).then((r) => r.data),
  standingChanges: (status) => client.get("/standing-changes", { params: status ? { status } : {} }).then((r) => r.data),
  standingSettings: () => client.get("/standing-settings").then((r) => r.data),
  standingSettingsSave: (d) => client.put("/standing-settings", d).then((r) => r.data),
  printAgentSettings: () => client.get("/print-agent/settings").then((r) => r.data),
  printAgentSave: (d) => client.put("/print-agent/settings", d).then((r) => r.data),
  printAgentTest: () => client.post("/print-agent/test").then((r) => r.data),
  orderMoveDept: (id, department) => client.patch(`/orders/${id}/department`, { department }).then((r) => r.data),
  ordersAutoRoute: () => client.post("/orders/auto-route").then((r) => r.data),
  dedupScan: (entity) => client.post("/dedup/scan", { entity }).then((r) => r.data),
  dedupResolve: (entity, remove_ids, keep_id) => client.post("/dedup/resolve", { entity, remove_ids, keep_id }).then((r) => r.data),
  dedupIgnore: (entity, ids) => client.post("/dedup/ignore", { entity, ids }).then((r) => r.data),
  recipesHealth: () => client.get("/recipes-health").then((r) => r.data),
  recipeLinkIngredient: (rid, row_index, ingredient_id) => client.post(`/recipes/${rid}/link-ingredient`, { row_index, ingredient_id }).then((r) => r.data),
  ingredientSetCost: (id, cost_per_unit) => client.post(`/ingredients/${id}/cost`, { cost_per_unit }).then((r) => r.data),
  standingUnlockRequests: () => client.get("/standing-unlock-requests").then((r) => r.data),
  standingSendPin: (rid) => client.post(`/standing-unlock-requests/${rid}/send-pin`).then((r) => r.data),
  standingProductionSheet: (date) => client.get("/standing-orders/production-sheet", { params: date ? { date } : {} }).then((r) => r.data),
  standingDeliveries: (date) => client.get("/standing-orders/deliveries", { params: date ? { date } : {} }).then((r) => r.data),
  standingDeliverySign: (payload) => client.post("/standing-orders/deliveries/sign", payload).then((r) => r.data),
  standingDeliverySignature: (date, partner_name) => client.get("/standing-orders/deliveries/signature", { params: { date, partner_name } }).then((r) => r.data),
  standingDeliveriesArchive: (days = 90) => client.get("/standing-orders/deliveries/archive", { params: { days } }).then((r) => r.data),
  standingDeliverySignLink: (payload) => client.post("/standing-orders/deliveries/sign-link", payload).then((r) => r.data),
  standingAdvanceTest: () => client.post("/standing-orders/advance-test").then((r) => r.data),
  standingMonthTotals: (month) => client.get("/standing-orders/month-totals", { params: month ? { month } : {} }).then((r) => r.data),
  publicSignInfo: (token) => client.get(`/public/delivery-sign/${token}`).then((r) => r.data),
  publicSignSubmit: (token, signature) => client.post(`/public/delivery-sign/${token}`, { signature }).then((r) => r.data),
  standingChangesHistory: (days = 180) => client.get(`/standing-changes-history?days=${days}`).then((r) => r.data),
  portalStandingSettings: (code) => client.get(`/portal/${code}/standing-settings`).then((r) => r.data),
  portalStandingUnlockReq: (code) => client.post(`/portal/${code}/standing-unlock-request`).then((r) => r.data),
  portalStandingUnlock: (code, pin) => client.post(`/portal/${code}/standing-unlock`, { pin }).then((r) => r.data),
  standingChangeApprove: (cid, effective_date) => client.post(`/standing-changes/${cid}/approve`, { effective_date }).then((r) => r.data),
  standingChangeReject: (cid) => client.post(`/standing-changes/${cid}/reject`).then((r) => r.data),
  portalStandingToggle: (code, sid, pin) => axios.post(`${API}/portal/${code}/standing-orders/${sid}/toggle`, null, { params: pin ? { pin } : {} }).then((r) => r.data),
  portalStandingDelete: (code, sid, pin) => axios.delete(`${API}/portal/${code}/standing-orders/${sid}`, { params: pin ? { pin } : {} }).then((r) => r.data),
  portalThread: (code) => axios.get(`${API}/portal/${code}/thread`).then((r) => r.data),
  portalThreadSend: (code, d) => axios.post(`${API}/portal/${code}/thread`, d).then((r) => r.data),

  // Custom orders / threads / price sync (business)
  quoteOrder: (oid, d) => client.put(`/orders/${oid}/quote`, d).then((r) => r.data),
  partnerThread: (pid) => client.get(`/partners/${pid}/thread`).then((r) => r.data),
  partnerThreadSend: (pid, d) => client.post(`/partners/${pid}/thread`, d).then((r) => r.data),
  threadsUnread: () => client.get("/partners/threads/unread").then((r) => r.data),
  ordersNewSince: (since) => client.get(`/orders/new-since${since ? `?since=${encodeURIComponent(since)}` : ""}`).then((r) => r.data),
  panettoniEnsure: () => client.post("/panettoni/ensure").then((r) => r.data),
  threadsSummary: () => client.get("/partners/threads/summary").then((r) => r.data),
  partnerCreditNote: (pid, d) => client.post(`/partners/${pid}/credit-note`, d).then((r) => r.data),
  priceSync: async (d) => {
    let prev = d.recipe_id ? await findIn("/recipes", d.recipe_id) : null;
    if (!prev && (d.name || "").trim()) {
      try {
        const all = (await client.get("/recipes")).data || [];
        prev = all.find((x) => (x.name || "").toLowerCase() === d.name.trim().toLowerCase()) || null;
      } catch { prev = null; }
    }
    const res = (await client.post("/price-sync", d)).data;
    if (prev) {
      const map = { catalog: "selling_price", wholesale: "wholesale_price", company: "company_price" };
      const restore = {};
      (d.targets || []).forEach((tg) => { if (map[tg]) restore[map[tg]] = +prev[map[tg]] || 0; });
      if (Object.keys(restore).length) {
        pushUndo(`${prev.name}${patchDesc(restore)}`, () => client.put(`/recipes/${prev.id}/catalog`, restore), () => client.post("/price-sync", d));
      }
    }
    return res;
  },
  archiveIngredient: (id, archived) => client.put(`/ingredients/${id}/archive`, { archived }).then((r) => r.data),
  notifyOrderReady: (oid) => client.post(`/orders/${oid}/notify-ready`).then((r) => r.data),
  ingredientCatalog: (id, catalog) => client.put(`/ingredients/${id}/catalog-assign`, { catalog }).then((r) => r.data),
  countryRules: () => client.get("/country-rules").then((r) => r.data),
  countryRulesSave: (d) => client.put("/country-rules", d).then((r) => r.data),
  countryRulesReset: () => client.delete("/country-rules").then((r) => r.data),
  helpVideos: () => client.get("/help/videos").then((r) => r.data),
  helpVideoSet: (section, url) => client.post("/help/videos", { section, url }).then((r) => r.data),
  demoReset: () => axios.post(`${API}/demo/reset`).then((r) => r.data),
};

export const photoUrl = (p) => (p ? `${API}/photos/${p}` : null);

export default api;
